import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import Modal from '../shared/Modal';

export default function OverheadsPage() {
  const { toast } = useToast();
  const [categories,    setCategories]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showForm,      setShowForm]      = useState(false);
  const [editing,       setEditing]       = useState(null);
  const [form,          setForm]          = useState(blankForm());
  const [saving,        setSaving]        = useState(false);
  const [invCats,       setInvCats]       = useState([]);
  const [invLoading,    setInvLoading]    = useState(true);
  const [showInvForm,   setShowInvForm]   = useState(false);
  const [editingInv,    setEditingInv]    = useState(null);
  const [invForm,       setInvForm]       = useState({ name: '' });
  const [invSaving,     setInvSaving]     = useState(false);

  const load = () => {
    setLoading(true);
    api.get('overhead-categories/all')
      .then(r => { setCategories(r); setLoading(false); })
      .catch(() => setLoading(false));
  };
  const loadInv = () => {
    setInvLoading(true);
    api.get('invoice-overhead-categories', { all: '1' })
      .then(r => { setInvCats(r || []); setInvLoading(false); })
      .catch(() => setInvLoading(false));
  };

  useEffect(() => { load(); loadInv(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setShowForm(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, code: cat.code, description: cat.description || '', parent_id: cat.parent_id || '' });
    setShowForm(true);
  };

  // Top-level categories that can be chosen as a parent (excludes the row being
  // edited so a category can't become its own parent).
  const parentOptions = categories.filter(c => !c.parent_id && (!editing || c.id !== editing.id));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`overhead-categories/${editing.id}`, form);
      } else {
        await api.post('overhead-categories', form);
      }
      load();
      setShowForm(false);
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  const toggleActive = async (cat) => {
    const action = cat.is_active ? 'deactivate' : 're-activate';
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} "${cat.name}"?`)) return;
    try {
      if (cat.is_active) {
        await api.delete(`overhead-categories/${cat.id}`);
      } else {
        await api.put(`overhead-categories/${cat.id}`, { is_active: 1 });
      }
      load();
    } catch (e) {
      toast.error('Failed: ' + e.message);
    }
  };

  const submitInv = async () => {
    if (!invForm.name.trim()) { toast.error('Name is required'); return; }
    setInvSaving(true);
    try {
      if (editingInv) {
        await api.put(`invoice-overhead-categories/${editingInv.id}`, invForm);
      } else {
        await api.post('invoice-overhead-categories', invForm);
      }
      loadInv();
      setShowInvForm(false);
      setEditingInv(null);
      setInvForm({ name: '' });
    } catch (e) {
      toast.error('Save failed: ' + (e.message || 'Unknown error'));
    }
    setInvSaving(false);
  };
  const toggleInv = async (cat) => {
    try {
      await api.put(`invoice-overhead-categories/${cat.id}`, { is_active: cat.is_active ? 0 : 1 });
      loadInv();
    } catch (e) { toast.error('Failed: ' + e.message); }
  };

  const active   = categories.filter(c => c.is_active);
  const inactive = categories.filter(c => !c.is_active);
  const invActive   = invCats.filter(c => c.is_active);
  const invInactive = invCats.filter(c => !c.is_active);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">Overhead Categories</h2>
          <div className="text-[9px] text-gray-500">Manage categories used for non-SOW time bookings</div>
        </div>
        <button
          onClick={openCreate}
          className="bg-navy text-white text-[10px] font-bold px-3 py-1.5 rounded hover:opacity-90 transition-opacity"
        >
          + Add Category
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[10px] text-gray-400">Loading…</div>
      ) : (
        <>
          <CategoryTable
            categories={active}
            title="Active"
            emptyText="No active categories — add one above."
            onEdit={openEdit}
            onToggle={toggleActive}
            toggleLabel="Deactivate"
            toggleStyle="text-red-500 hover:text-red-700"
          />
          {inactive.length > 0 && (
            <CategoryTable
              categories={inactive}
              title="Inactive"
              emptyText=""
              onEdit={openEdit}
              onToggle={toggleActive}
              toggleLabel="Re-activate"
              toggleStyle="text-emerald-600 hover:text-emerald-800"
            />
          )}
        </>
      )}

      {/* ── Invoice Overhead Categories ─────────────────────────────────────── */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-extrabold text-navy">Invoice Overhead Categories</h2>
            <div className="text-[9px] text-gray-500">Categories for classifying supplier invoices not tied to a SOW or PO</div>
          </div>
          <button onClick={() => { setEditingInv(null); setInvForm({ name: '' }); setShowInvForm(true); }}
            className="bg-navy text-white text-[10px] font-bold px-3 py-1.5 rounded hover:opacity-90">
            + Add Category
          </button>
        </div>
        {invLoading ? (
          <div className="text-center py-6 text-[10px] text-gray-400">Loading…</div>
        ) : (
          <>
            <div className="bg-white border border-gray-200 rounded overflow-hidden mb-4">
              {invActive.length === 0 ? (
                <div className="text-center py-6 text-[10px] text-gray-400">No active categories</div>
              ) : (
                <div className="overflow-x-auto"><table className="w-full">
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Name</th>
                      <th className="px-3 py-2 border-b bg-gray-50 w-28" />
                    </tr>
                  </thead>
                  <tbody>
                    {invActive.map(cat => (
                      <tr key={cat.id} className="hover:bg-gray-50 border-b border-gray-50 last:border-0">
                        <td className="px-3 py-2 text-[10px] font-semibold text-navy">{cat.name}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => { setEditingInv(cat); setInvForm({ name: cat.name }); setShowInvForm(true); }}
                            className="text-[9px] text-gold font-semibold hover:underline mr-3">Edit</button>
                          <button onClick={() => toggleInv(cat)}
                            className="text-[9px] text-red-500 font-semibold hover:underline">Deactivate</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
            {invInactive.length > 0 && (
              <div className="mb-4">
                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-1">Inactive</div>
                <div className="bg-white border border-gray-200 rounded overflow-x-auto">
                  <table className="w-full">
                    <tbody>
                      {invInactive.map(cat => (
                        <tr key={cat.id} className="hover:bg-gray-50 border-b border-gray-50 last:border-0">
                          <td className="px-3 py-2 text-[10px] text-gray-400">{cat.name}</td>
                          <td className="px-3 py-2 text-right">
                            <button onClick={() => toggleInv(cat)}
                              className="text-[9px] text-emerald-600 font-semibold hover:underline">Re-activate</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Invoice category create/edit modal */}
      {showInvForm && (
        <Modal title={editingInv ? 'Edit Invoice Category' : 'New Invoice Overhead Category'} onClose={() => setShowInvForm(false)}>
            <div className="mb-4">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Name *</label>
              <input value={invForm.name} onChange={e => setInvForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:border-gold"
                placeholder="e.g. Office" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowInvForm(false)} className="flex-1 text-[11px] border border-gray-200 rounded py-2 font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
              <button onClick={submitInv} disabled={invSaving} className="flex-1 text-[11px] bg-navy text-white rounded py-2 font-bold hover:opacity-90 disabled:opacity-50">
                {invSaving ? 'Saving…' : editingInv ? 'Save Changes' : 'Create'}
              </button>
            </div>
        </Modal>
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <Modal title={editing ? 'Edit Category' : 'New Overhead Category'} onClose={() => setShowForm(false)}>
            <div className="mb-3">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Parent category</label>
              <select
                value={form.parent_id}
                onChange={e => setForm(f => ({ ...f, parent_id: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:border-gold"
              >
                <option value="">— None (top-level category) —</option>
                {parentOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="text-[9px] text-gray-400 mt-1">Leave blank for a top-level category, or pick a parent to make this a sub-category.</p>
            </div>

            <div className="mb-3">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Name *</label>
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:border-gold"
                placeholder="e.g. Training"
              />
            </div>

            <div className="mb-3">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Code *</label>
              <input
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] font-mono focus:outline-none focus:border-gold"
                placeholder="e.g. TRAINING"
                maxLength={20}
              />
              <p className="text-[9px] text-gray-400 mt-1">Uppercase letters, numbers and underscores only.</p>
            </div>

            <div className="mb-4">
              <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] h-16 resize-none focus:outline-none focus:border-gold"
                placeholder="Optional description of when to use this category"
              />
            </div>

            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} className="flex-1 text-[11px] border border-gray-200 rounded py-2 font-semibold text-gray-500 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={submit} disabled={saving} className="flex-1 text-[11px] bg-navy text-white rounded py-2 font-bold hover:opacity-90 disabled:opacity-50">
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create'}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

// ── Table sub-component ────────────────────────────────────────────────────────

function CategoryTable({ categories, title, emptyText, onEdit, onToggle, toggleLabel, toggleStyle }) {
  return (
    <div className="mb-4">
      <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-1">{title}</div>
      <div className="bg-white border border-gray-200 rounded overflow-hidden">
        {categories.length === 0 ? (
          <div className="text-center py-6 text-[10px] text-gray-400">{emptyText}</div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead>
              <tr>
                <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Name</th>
                <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Code</th>
                <th className="px-3 py-2 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 hidden sm:table-cell">Description</th>
                <th className="px-3 py-2 border-b bg-gray-50 w-24" />
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => (
                <tr key={cat.id} className="hover:bg-gray-50 border-b border-gray-50 last:border-0">
                  <td className={`px-3 py-2 text-[10px] ${cat.parent_id ? 'text-gray-600' : 'font-semibold text-navy'}`}>
                    {cat.parent_id
                      ? <span className="pl-4 text-gray-400">↳ <span className="text-gray-700">{cat.name}</span></span>
                      : cat.name}
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{cat.code}</span>
                  </td>
                  <td className="px-3 py-2 text-[9px] text-gray-400 hidden sm:table-cell">{cat.description || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onEdit(cat)} className="text-[9px] text-gold font-semibold hover:underline mr-3">Edit</button>
                    <button onClick={() => onToggle(cat)} className={`text-[9px] font-semibold hover:underline ${toggleStyle}`}>{toggleLabel}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function blankForm() {
  return { name: '', code: '', description: '', parent_id: '' };
}
